import React, { useState, useEffect } from 'react';
import { History, Download, Wand2, FileText, RefreshCw, Presentation, ExternalLink, Copy, Clock, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HistoryTab = ({ 
  token,
  reports, 
  setCurrentReport, 
  setActiveTab, 
  exportPDF 
}) => {
  const [magicReports, setMagicReports] = useState([]);
  const [gammaReports, setGammaReports] = useState([]);
  const [loadingMagic, setLoadingMagic] = useState(false);
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    fetchAllHistory();
  }, [token]);

  const fetchAllHistory = async () => {
    if (!token) return;
    setLoadingMagic(true);
    try {
      const [magicRes, gammaRes] = await Promise.all([
        axios.get(`${API_URL}/api/reports/magic/history`, authHeaders),
        axios.get(`${API_URL}/api/reports/gamma/history`, authHeaders),
      ]);
      setMagicReports(magicRes.data.reports || []);
      setGammaReports(gammaRes.data.reports || []);
    } catch (err) {
      console.error('Error fetching history:', err);
    } finally {
      setLoadingMagic(false);
    }
  };

  const downloadMagicReport = async (report) => {
    if (!report?.url) {
      toast.error('Lien de telechargement non disponible');
      return;
    }
    try {
      const response = await axios.get(`${API_URL}${report.url}`, {
        ...authHeaders,
        responseType: 'blob',
        validateStatus: (status) => status < 500,
      });
      if (response.status === 404) {
        toast.error('Ce rapport n\'est plus disponible. Regenerez-le depuis l\'onglet Rapport.');
        return;
      }
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = report.filename || `magic_report.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF telecharge');
    } catch {
      toast.error('Erreur telechargement — le rapport doit etre regenere');
    }
  };

  const downloadStandardPdf = async (reportId) => {
    try {
      toast.info('Generation du PDF...');
      const response = await axios.get(`${API_URL}/api/reports/${reportId}/pdf`, {
        ...authHeaders,
        responseType: 'blob',
        validateStatus: (status) => status < 500,
      });
      if (response.status === 404) {
        toast.error('Rapport non trouve dans la base');
        return;
      }
      if (response.status >= 400) {
        toast.error('Erreur lors de la generation du PDF');
        return;
      }
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `rapport-cesm-${reportId.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF telecharge');
    } catch (err) {
      console.error('PDF export error:', err);
      toast.error('Erreur export PDF');
    }
  };

  const getEwsColor = (code) => {
    switch(code) {
      case 'GREEN': return 'text-green-400 border-green-500/40';
      case 'YELLOW': return 'text-yellow-400 border-yellow-500/40';
      case 'ORANGE': return 'text-orange-400 border-orange-500/40';
      case 'RED': return 'text-red-400 border-red-500/40';
      default: return 'text-slate-400 border-slate-500/40';
    }
  };

  return (
    <div className="space-y-6" data-testid="history-tab">

      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="font-mono text-marine-cyan text-lg font-bold uppercase tracking-wider flex items-center gap-2">
          <History className="w-5 h-5" /> Archives des Rapports
        </h2>
        <button 
          onClick={fetchAllHistory}
          disabled={loadingMagic}
          className="p-2 text-white/40 hover:text-white rounded transition-all"
          data-testid="refresh-history"
        >
          <RefreshCw className={`w-4 h-4 ${loadingMagic ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Magic Reports */}
      <div>
        <h3 className="font-mono text-purple-400 text-sm font-bold uppercase tracking-wider flex items-center gap-2 mb-3">
          <Wand2 className="w-4 h-4" /> Magic Reports ({magicReports.length})
        </h3>
        {magicReports.length > 0 ? (
          <div className="space-y-2">
            {magicReports.map((r, i) => (
              <div key={r.id || i} className="flex items-center justify-between bg-black/40 border border-purple-500/20 rounded-lg p-3 hover:border-purple-500/40 transition-all">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-500/10 rounded">
                    <FileText className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="font-mono text-purple-300 text-sm font-bold">{r.id || 'Magic Report'}</p>
                    <p className="font-mono text-white/40 text-xs flex items-center gap-2">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(r.created_at).toLocaleString('fr-FR')}
                      </span>
                      <span>{r.theater_id?.toUpperCase() || 'HORMUZ'}</span>
                      <span>{r.statistics?.news_analyzed || 0} articles</span>
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {r.statistics?.ews_code && (
                    <span className={`px-2 py-0.5 border rounded font-mono text-xs ${getEwsColor(r.statistics.ews_code)}`}>
                      EWS {r.statistics.ews_score || 0}
                    </span>
                  )}
                  <button
                    onClick={() => downloadMagicReport(r)}
                    className="px-3 py-1.5 bg-purple-500/15 border border-purple-500/30 text-purple-300 hover:bg-purple-500/25 font-mono text-xs rounded flex items-center gap-1.5 transition-all"
                    data-testid={`download-magic-${i}`}
                  >
                    <Download className="w-3.5 h-3.5" /> PDF
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-black/30 border border-purple-500/10 rounded-lg p-6 text-center">
            <Wand2 className="w-8 h-8 text-purple-500/30 mx-auto mb-2" />
            <p className="font-mono text-white/30 text-sm">Aucun Magic Report</p>
          </div>
        )}
      </div>

      {/* Gamma Presentations */}
      <div>
        <h3 className="font-mono text-orange-400 text-sm font-bold uppercase tracking-wider flex items-center gap-2 mb-3">
          <Presentation className="w-4 h-4" /> Presentations Gamma ({gammaReports.length})
        </h3>
        {gammaReports.length > 0 ? (
          <div className="space-y-2">
            {gammaReports.map((r, i) => (
              <div key={r.generation_id || i} className="flex items-center justify-between bg-black/40 border border-orange-500/20 rounded-lg p-3 hover:border-orange-500/40 transition-all">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-500/10 rounded">
                    <Presentation className="w-5 h-5 text-orange-400" />
                  </div>
                  <div>
                    <p className="font-mono text-orange-300 text-sm font-bold">
                      {r.report_type === 'executive' ? 'Executif' : r.report_type === 'strategic' ? 'Strategique' : 'Technique'}
                    </p>
                    <p className="font-mono text-white/40 text-xs flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(r.created_at).toLocaleString('fr-FR')}
                      {r.created_by && <span className="ml-2">{r.created_by}</span>}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => { navigator.clipboard.writeText(r.gamma_url); toast.success('Lien copie'); }}
                    className="p-1.5 text-white/30 hover:text-white transition-all"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                  <a
                    href={r.gamma_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-1.5 bg-orange-500/15 border border-orange-500/30 text-orange-300 hover:bg-orange-500/25 font-mono text-xs rounded flex items-center gap-1.5 transition-all"
                    data-testid={`open-gamma-${i}`}
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> Ouvrir
                  </a>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-black/30 border border-orange-500/10 rounded-lg p-6 text-center">
            <Presentation className="w-8 h-8 text-orange-500/30 mx-auto mb-2" />
            <p className="font-mono text-white/30 text-sm">Aucune presentation Gamma</p>
          </div>
        )}
      </div>

      {/* Standard Reports */}
      {reports && reports.length > 0 && (
        <div>
          <h3 className="font-mono text-marine-cyan text-sm font-bold uppercase tracking-wider flex items-center gap-2 mb-3">
            <FileText className="w-4 h-4" /> Rapports Standard ({reports.length})
          </h3>
          <div className="space-y-2">
            {reports.map((r, i) => (
              <div key={r.id || i} className="flex items-center justify-between bg-black/40 border border-marine-cyan/20 rounded-lg p-3 hover:border-marine-cyan/40 transition-all">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-marine-cyan/10 rounded">
                    <FileText className="w-5 h-5 text-marine-cyan" />
                  </div>
                  <div>
                    <p className="font-mono text-marine-cyan text-sm font-bold">{r.report_number || r.id}</p>
                    <p className="font-mono text-white/40 text-xs flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(r.created_at).toLocaleString('fr-FR')}
                      {r.title && <span className="ml-2 text-white/30">{r.title.substring(0, 40)}</span>}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => downloadStandardPdf(r.id)}
                  className="px-3 py-1.5 bg-marine-cyan/15 border border-marine-cyan/30 text-marine-cyan hover:bg-marine-cyan/25 font-mono text-xs rounded flex items-center gap-1.5 transition-all"
                  data-testid={`download-standard-${i}`}
                >
                  <Download className="w-3.5 h-3.5" /> PDF
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info */}
      <div className="p-3 bg-amber-500/5 border border-amber-500/20 rounded flex items-start gap-2 text-xs">
        <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
        <p className="font-mono text-white/50">
          Les Magic Reports PDF sont stockes temporairement sur le serveur. Si le telechargement echoue, regenerez le rapport depuis l'onglet <strong className="text-amber-400">Rapport</strong>.
          Les presentations Gamma restent accessibles via leur lien permanent.
        </p>
      </div>
    </div>
  );
};

export default HistoryTab;
